import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import { listInsurers } from "@/core/insurance/insurer-store";
import {
  activatePartnershipWithCode,
  defaultInsuranceHubUrl,
  generatePartnershipRequest,
  listHospitalPartnerships,
  revokeHospitalPartnership,
} from "@/core/insurance/partnership-store";
import { revokePartnershipOnHubApi } from "@/core/insurance/hub-api-client";
import { routes } from "@/router/all_routes";

const PARTNERSHIP_STATUS_LABELS: Record<string, string> = {
  pending: "En attente",
  active: "Actif",
  revoked: "Révoqué",
};

export function InsurancePartnershipsPage() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [insurerId, setInsurerId] = useState("");
  const [approvalCode, setApprovalCode] = useState("");
  const [requestKey, setRequestKey] = useState<string | null>(null);
  const [acceptUrl, setAcceptUrl] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [revokeBusyId, setRevokeBusyId] = useState<string | null>(null);

  useEffect(() => {
    const refresh = () => setRefreshKey((k) => k + 1);
    window.addEventListener("hospitol:insurance-partnerships", refresh);
    return () => window.removeEventListener("hospitol:insurance-partnerships", refresh);
  }, []);

  const insurers = useMemo(() => {
    void refreshKey;
    return listInsurers(true);
  }, [refreshKey]);

  useEffect(() => {
    if (!insurerId && insurers[0]) setInsurerId(insurers[0].id);
  }, [insurers, insurerId]);

  const partnerships = useMemo(() => {
    void refreshKey;
    return listHospitalPartnerships();
  }, [refreshKey]);

  const pagination = useClientPagination(partnerships, [], 25);
  const { pagedItems } = pagination;

  const activeForInsurer = partnerships.find(
    (p) => p.insurerId === insurerId && p.status === "active",
  );

  const handleGenerateKey = () => {
    setMsg(null);
    const result = generatePartnershipRequest(insurerId);
    if (!result) {
      setMsg("Assureur introuvable.");
      return;
    }
    setRequestKey(result.requestKey);
    setAcceptUrl(result.acceptUrl);
    setMsg("Clé générée — envoyez le lien à l'assureur pour approbation.");
  };

  const handleActivate = () => {
    setMsg(null);
    const partnership = activatePartnershipWithCode(insurerId, approvalCode);
    if (!partnership) {
      setMsg("Code d'approbation invalide.");
      return;
    }
    setApprovalCode("");
    setRequestKey(null);
    setAcceptUrl(null);
    setMsg(`Partenariat actif avec ${partnership.hospitalName}.`);
    setRefreshKey((k) => k + 1);
  };

  const handleRevoke = (partnershipId: string) => {
    if (!window.confirm("Révoquer ce partenariat ? Les factures assureur ne pourront plus être publiées sur le hub.")) {
      return;
    }
    setRevokeBusyId(partnershipId);
    void revokePartnershipOnHubApi(partnershipId).finally(() => {
      const revoked = revokeHospitalPartnership(partnershipId);
      setRevokeBusyId(null);
      if (!revoked) {
        setMsg("Impossible de révoquer ce partenariat.");
        return;
      }
      setMsg("Partenariat révoqué.");
      setRefreshKey((k) => k + 1);
    });
  };

  const hubUrl = defaultInsuranceHubUrl();

  return (
    <div>
      <PageHeader
        title="Partenariats assureurs"
        description="Liez votre hôpital aux compagnies via le hub central — sans compte assureur sur ce tenant."
        actions={
          <a
            href={hubUrl}
            target="_blank"
            rel="noreferrer"
            className="btn btn-outline-primary btn-sm"
          >
            Ouvrir le hub assureur
          </a>
        }
      />

      {msg && <div className="alert alert-info py-2">{msg}</div>}

      <div className="row g-3 mb-4">
        <div className="col-lg-6">
          <div className="ph-invoice-sheet h-100">
            <h3 className="h6 fw-semibold mb-3">1. Générer une clé de liaison</h3>
            <label className="form-label small" htmlFor="partnership-insurer">
              Assureur cible
            </label>
            <select
              id="partnership-insurer"
              className="form-select form-select-sm mb-3"
              value={insurerId}
              onChange={(e) => setInsurerId(e.target.value)}
            >
              {insurers.map((ins) => (
                <option key={ins.id} value={ins.id}>
                  {ins.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={handleGenerateKey}
              disabled={!!activeForInsurer}
            >
              Générer la clé
            </button>
            {activeForInsurer && (
              <p className="small text-success mt-2 mb-0">
                Partenariat déjà actif pour cet assureur.
              </p>
            )}
            {requestKey && (
              <div className="mt-3">
                <div className="small text-muted mb-1">Clé (ITINS-REQ-…)</div>
                <div
                  className="p-2 bg-light border rounded small font-monospace"
                  style={{ wordBreak: "break-all" }}
                >
                  {requestKey}
                </div>
                {acceptUrl && (
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => navigator.clipboard.writeText(requestKey)}
                    >
                      Copier la clé
                    </button>
                    <a
                      href={acceptUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-outline-primary btn-sm"
                    >
                      Lien d&apos;approbation assureur
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="col-lg-6">
          <div className="ph-invoice-sheet h-100">
            <h3 className="h6 fw-semibold mb-3">2. Activer avec le code retour</h3>
            <p className="small text-muted">
              Après approbation sur le hub, l&apos;assureur vous renvoie un code ITINS-OK-….
            </p>
            <input
              className="form-control form-control-sm font-monospace mb-3"
              placeholder="ITINS-OK-…"
              value={approvalCode}
              onChange={(e) => setApprovalCode(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-success btn-sm"
              onClick={handleActivate}
              disabled={!approvalCode.trim()}
            >
              Activer le partenariat
            </button>
          </div>
        </div>
      </div>

      <div className="ph-invoice-sheet">
        <h3 className="h6 fw-semibold mb-3">Partenariats actifs</h3>
        {partnerships.length === 0 ? (
          <p className="text-muted mb-0">Aucun partenariat — générez une clé pour commencer.</p>
        ) : (
          <div className="table-responsive ph-diag-table">
            <table className="table table-sm ph-dash-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Assureur (org hub)</th>
                  <th>Statut</th>
                  <th>Approuvé le</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pagedItems.map((p) => {
                  const insurer = insurers.find((i) => i.id === p.insurerId);
                  return (
                    <tr key={p.id} className="ph-diag-row">
                      <td data-label="Assureur">
                        {insurer?.name ?? p.insurerOrgId}
                        <span className="d-block small text-muted font-monospace">
                          {p.insurerOrgId}
                        </span>
                      </td>
                      <td data-label="Statut">
                        <span className={`hosp-ins-badge hosp-ins-badge--${p.status}`}>
                          {PARTNERSHIP_STATUS_LABELS[p.status] ?? p.status}
                        </span>
                      </td>
                      <td data-label="Approuvé le" className="small">
                        {p.approvedAt
                          ? new Date(p.approvedAt).toLocaleString("fr-FR")
                          : "—"}
                      </td>
                      <td data-label="Action" className="text-end">
                        {p.status === "active" && (
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            disabled={revokeBusyId === p.id}
                            onClick={() => handleRevoke(p.id)}
                          >
                            {revokeBusyId === p.id ? "…" : "Révoquer"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <TablePagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalItems={pagination.totalItems}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
            />
          </div>
        )}
        <p className="small text-muted mt-3 mb-0">
          Une fois lié, soumettez les dossiers depuis{" "}
          <Link to={routes.insuranceCases}>Prises en charge</Link> vers le hub assureur.
        </p>
      </div>
    </div>
  );
}
